import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { getPlanByProductId } from "@/lib/stripe-plans";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Play, Pause, Archive, Trash2, Copy, Pencil, Workflow, Loader2, BarChart3, Megaphone } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AutomationWizard } from "@/components/automations/AutomationWizard";
import { AutomationBuilder } from "@/components/automations/AutomationBuilder";
import { AutomationAnalytics } from "@/components/automations/AutomationAnalytics";
import { AutomationCampaignAnalytics } from "@/components/automations/AutomationCampaignAnalytics";
import { CampaignWizard } from "@/components/automations/CampaignWizard";
import { remapCompiledFlowIds } from "@/lib/automation-flow";
import { setAutomationAndCampaignStatus } from "@/lib/automation-campaign";

const AUTOMATION_ALLOWED_PLANS = ["intermediario", "pro", "enterprise"];

interface Automation {
  id: string;
  name: string;
  channel: string;
  status: string;
  priority: number;
  updated_at: string;
  created_at: string;
}

const channelLabels: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram_dm: "Instagram DM",
  instagram_story: "Instagram Story",
  instagram_comment: "Instagram Comentário",
  messenger: "Messenger",
  crm: "CRM",
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Rascunho", variant: "secondary" },
  active: { label: "Ativa", variant: "default" },
  paused: { label: "Pausada", variant: "outline" },
  archived: { label: "Arquivada", variant: "destructive" },
};

export default function Automations() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { productId } = useSubscription();
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [campaignWizardOpen, setCampaignWizardOpen] = useState(false);
  const [campaignRefreshKey, setCampaignRefreshKey] = useState(0);

  const editId = searchParams.get("edit");

  // Plan gating
  const plan = productId ? getPlanByProductId(productId) : null;
  const hasAccess = plan && AUTOMATION_ALLOWED_PLANS.includes(plan);

  useEffect(() => {
    if (!hasAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [hasAccess, navigate]);

  useEffect(() => {
    if (!workspaceId) return;
    fetchAutomations();
  }, [workspaceId]);

  const fetchAutomations = async () => {
    if (!workspaceId) return;
    setLoading(true);

    const { data: campaignLinks } = await (supabase as any)
      .from("automation_campaigns")
      .select("automation_id")
      .eq("workspace_id", workspaceId);

    const campaignAutomationIds = new Set<string>(
      ((campaignLinks || []) as { automation_id: string }[]).map((r) => r.automation_id),
    );

    const { data, error } = await supabase
      .from("automations")
      .select("id, name, channel, status, priority, updated_at, created_at")
      .eq("workspace_id", workspaceId)
      .order("priority", { ascending: true })
      .order("created_at", { ascending: true });

    if (!error && data) {
      setAutomations(
        (data as unknown as Automation[]).filter((a) => !campaignAutomationIds.has(a.id)),
      );
    }
    setLoading(false);
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    const result = await setAutomationAndCampaignStatus(supabase, id, newStatus);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    toast.success(`Automação ${statusConfig[newStatus]?.label.toLowerCase() || newStatus}`);
    fetchAutomations();
  };

  const handleDuplicate = async (automation: Automation) => {
    if (!workspaceId) return;

    const limitCheck = await checkCanCreate("automations");
    if (!limitCheck.can_create) {
      toast.error(`Limite de automações atingido (${limitCheck.current}/${limitCheck.limit})`);
      return;
    }

    try {
      const { data: source, error: sourceError } = await supabase
        .from("automations")
        .select(
          "channel, priority, trigger_type, trigger_config, ai_control_mode, cooldown_seconds, max_executions_per_contact, max_continuous_duration_minutes, compiled_flow",
        )
        .eq("id", automation.id)
        .single();

      if (sourceError || !source) {
        toast.error(sourceError?.message || "Automação não encontrada");
        return;
      }

      const src = source as Record<string, unknown>;

      const { data: newAuto, error: insertError } = await supabase
        .from("automations")
        .insert({
          workspace_id: workspaceId,
          name: `${automation.name} (cópia)`,
          channel: src.channel,
          status: "draft",
          priority: src.priority ?? automation.priority,
          trigger_type: src.trigger_type,
          trigger_config: src.trigger_config,
          ai_control_mode: src.ai_control_mode,
          cooldown_seconds: src.cooldown_seconds ?? 0,
          max_executions_per_contact: src.max_executions_per_contact ?? 0,
          max_continuous_duration_minutes: src.max_continuous_duration_minutes ?? 30,
        } as any)
        .select("id")
        .single();

      if (insertError || !newAuto) {
        toast.error(insertError?.message || "Erro ao duplicar");
        return;
      }

      const newAutomationId = (newAuto as { id: string }).id;

      const { data: srcNodes } = await supabase
        .from("automation_nodes")
        .select("id, node_type, position_x, position_y, config, label")
        .eq("automation_id", automation.id)
        .order("position_y", { ascending: true });

      const { data: srcEdges } = await supabase
        .from("automation_edges")
        .select("source_node_id, target_node_id, branch")
        .eq("automation_id", automation.id);

      const idMap = new Map<string, string>();

      if (srcNodes && srcNodes.length > 0) {
        const nodePayload = srcNodes.map((n) => ({
          automation_id: newAutomationId,
          workspace_id: workspaceId,
          node_type: n.node_type,
          position_x: n.position_x,
          position_y: n.position_y,
          config: n.config,
          label: n.label,
        }));

        const { data: insertedNodes, error: nodesError } = await supabase
          .from("automation_nodes")
          .insert(nodePayload as any)
          .select("id");

        if (nodesError) throw nodesError;

        srcNodes.forEach((n, i) => {
          const inserted = (insertedNodes as { id: string }[])[i];
          if (inserted) idMap.set(n.id, inserted.id);
        });

        if (srcEdges && srcEdges.length > 0) {
          const edgePayload = srcEdges
            .filter(
              (e) => idMap.has(e.source_node_id) && idMap.has(e.target_node_id),
            )
            .map((e) => ({
              automation_id: newAutomationId,
              workspace_id: workspaceId,
              source_node_id: idMap.get(e.source_node_id)!,
              target_node_id: idMap.get(e.target_node_id)!,
              branch: e.branch,
            }));

          if (edgePayload.length > 0) {
            const { error: edgesError } = await supabase
              .from("automation_edges")
              .insert(edgePayload as any);
            if (edgesError) throw edgesError;
          }
        }
      }

      const compiled = src.compiled_flow as {
        nodes: Array<{ id: string }>;
        edges: Array<{ source: string; target: string; branch: string }>;
      } | null;

      if (compiled?.nodes?.length && idMap.size > 0) {
        const remapped = remapCompiledFlowIds(compiled, idMap);
        await supabase
          .from("automations")
          .update({ compiled_flow: remapped } as any)
          .eq("id", newAutomationId);
      } else if (compiled?.nodes?.length) {
        await supabase
          .from("automations")
          .update({ compiled_flow: compiled } as any)
          .eq("id", newAutomationId);
      }

      const { data: srcCampaign } = await (supabase as any)
        .from("automation_campaigns")
        .select("*")
        .eq("automation_id", automation.id)
        .maybeSingle();

      if (srcCampaign) {
        const c = srcCampaign as Record<string, unknown>;
        await (supabase as any).from("automation_campaigns").insert({
          workspace_id: workspaceId,
          automation_id: newAutomationId,
          name: `${String(c.name)} (cópia)`,
          channel: c.channel,
          keywords: c.keywords,
          status: "draft",
          utm_source: c.utm_source,
          utm_medium: c.utm_medium,
          utm_campaign: c.utm_campaign,
          ad_set_name: c.ad_set_name,
          ad_name: c.ad_name,
          notes: c.notes,
          integration_id: c.integration_id ?? null,
        });
      }

      toast.success("Automação duplicada com fluxo");
      fetchAutomations();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erro ao duplicar";
      toast.error(message);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("automations")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Automação excluída");
      fetchAutomations();
    }
  };

  const handleNewAutomation = async () => {
    const limitCheck = await checkCanCreate("automations");
    if (!limitCheck.can_create) {
      toast.error(`Limite de automações atingido (${limitCheck.current}/${limitCheck.limit})`);
      return;
    }
    setWizardOpen(true);
  };

  const handleEdit = (id: string) => {
    setSearchParams({ edit: id });
  };

  if (!hasAccess) return null;

  // Show builder if editing
  if (editId) {
    return <AutomationBuilder automationId={editId} />;
  }

  return (
    <div className="flex-1 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Automações</h1>
          <p className="text-muted-foreground">
            Crie fluxos automatizados para engajar seus contatos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCampaignWizardOpen(true)} className="gap-2">
            <Megaphone className="h-4 w-4" />
            Nova campanha
          </Button>
          <Button onClick={handleNewAutomation} className="gap-2">
            <Plus className="h-4 w-4" />
            Nova automação
          </Button>
        </div>
      </div>

      <Tabs defaultValue="list" className="space-y-4">
        <TabsList className="h-9">
          <TabsTrigger value="list" className="gap-1.5">
            <Workflow className="h-3.5 w-3.5 shrink-0" />
            Automações
          </TabsTrigger>
          <TabsTrigger value="overview" className="gap-1.5">
            <BarChart3 className="h-3.5 w-3.5 shrink-0" />
            Geral
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-1.5">
            <Megaphone className="h-3.5 w-3.5 shrink-0" />
            Campanhas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : automations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Workflow className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">Nenhuma automação criada</h3>
              <p className="text-muted-foreground mb-4 max-w-sm">
                Crie sua primeira automação para engajar contatos automaticamente via WhatsApp, Instagram ou Messenger.
              </p>
              <Button onClick={handleNewAutomation} className="gap-2">
                <Plus className="h-4 w-4" />
                Criar automação
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Prioridade</TableHead>
                    <TableHead>Atualizado</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {automations.map((a) => {
                    const sc = statusConfig[a.status] || { label: a.status, variant: "secondary" as const };
                    return (
                      <TableRow key={a.id} className="cursor-pointer" onClick={() => handleEdit(a.id)}>
                        <TableCell className="font-medium">{a.name}</TableCell>
                        <TableCell>{channelLabels[a.channel] || a.channel}</TableCell>
                        <TableCell>
                          <Badge variant={sc.variant}>{sc.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{a.priority}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {format(new Date(a.updated_at), "dd MMM yyyy", { locale: ptBR })}
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(a.id)} className="gap-2">
                                <Pencil className="h-4 w-4" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(a)} className="gap-2">
                                <Copy className="h-4 w-4" /> Duplicar
                              </DropdownMenuItem>
                              {a.status === "active" ? (
                                <DropdownMenuItem onClick={() => handleStatusChange(a.id, "paused")} className="gap-2">
                                  <Pause className="h-4 w-4" /> Pausar
                                </DropdownMenuItem>
                              ) : a.status !== "archived" ? (
                                <DropdownMenuItem onClick={() => handleStatusChange(a.id, "active")} className="gap-2">
                                  <Play className="h-4 w-4" /> Ativar
                                </DropdownMenuItem>
                              ) : null}
                              {a.status !== "archived" && (
                                <DropdownMenuItem onClick={() => handleStatusChange(a.id, "archived")} className="gap-2">
                                  <Archive className="h-4 w-4" /> Arquivar
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleDelete(a.id)} className="gap-2 text-destructive">
                                <Trash2 className="h-4 w-4" /> Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="overview">
          {workspaceId && <AutomationAnalytics workspaceId={workspaceId} />}
        </TabsContent>

        <TabsContent value="campaigns">
          {workspaceId && (
            <AutomationCampaignAnalytics
              key={campaignRefreshKey}
              workspaceId={workspaceId}
            />
          )}
        </TabsContent>
      </Tabs>

      {workspaceId && (
        <>
          <AutomationWizard
            open={wizardOpen}
            onOpenChange={setWizardOpen}
            workspaceId={workspaceId}
            onCreated={fetchAutomations}
          />
          <CampaignWizard
            open={campaignWizardOpen}
            onOpenChange={setCampaignWizardOpen}
            workspaceId={workspaceId}
            onCreated={() => setCampaignRefreshKey((k) => k + 1)}
          />
        </>
      )}
    </div>
  );
}
